/**
 * Upload Schema Registry
 *
 * Central registry of all entity upload schemas.
 * Import this to get the schema for any V2 entity type.
 */

import { UploadSchema } from '../types';
import { v2ActivitySchema } from './v2Activity';
import { v2ConditionSchema } from './v2Condition';
import { v2ContractSchema } from './v2Contract';
import { v2ComponentSchema } from './v2Component';
import { v2CategorySchema } from './v2Category';
import { v2ExpenseTypeSchema } from './v2ExpenseType';
import { v2SpendPlanSchema } from './v2SpendPlan';
import { v2VoucherSchema } from './v2Voucher';
import { v2WorkPlanSchema } from './v2WorkPlan';
import { v1EntitySchema } from './v1Entity';
import { v1SupplierSchema } from './v1Supplier';
import { v1BeneficiarySchema } from './v1Beneficiary';

/** All registered upload schemas, keyed by entityType */
export const uploadSchemaRegistry: Record<string, UploadSchema> = {
	v2Activity: v2ActivitySchema,
	v2Category: v2CategorySchema,
	v2Component: v2ComponentSchema,
	v2Condition: v2ConditionSchema,
	v2Contract: v2ContractSchema,
	v2ExpenseType: v2ExpenseTypeSchema,
	v2SpendPlan: v2SpendPlanSchema,
	v2Voucher: v2VoucherSchema,
	v2WorkPlan: v2WorkPlanSchema,
	v2Entity: v1EntitySchema,
	v2Supplier: v1SupplierSchema,
	v1Supplier: v1SupplierSchema,
	v1Beneficiary: v1BeneficiarySchema,
};

/** Get a schema by entity type, or throw */
export function getUploadSchema(entityType: string): UploadSchema {
	const schema = uploadSchemaRegistry[entityType];
	if (!schema) {
		throw new Error(
			`No upload schema registered for entity type "${entityType}". ` +
				`Available: ${Object.keys(uploadSchemaRegistry).join(', ')}`
		);
	}
	return schema;
}

/**
 * Map a RelationDef.entityType (e.g. 'workPlan') to the schema used for inline creation.
 * If the registered schema has an `inlineCreationSchema`, that is returned instead
 * (e.g., v2Contract link schema → V1 contract creation schema).
 * Returns null if no schema exists for that relation target.
 */
export function getSchemaForRelation(relationEntityType: string): UploadSchema | null {
	const key = `v2${relationEntityType.charAt(0).toUpperCase()}${relationEntityType.slice(1)}`;
	const schema = uploadSchemaRegistry[key] ?? null;
	if (!schema) return null;
	return schema.inlineCreationSchema ?? schema;
}

/**
 * Check whether a target entity can be created inline during resolution.
 * Returns { canCreate: true, schema } if all required relation fields in the target schema
 * can be satisfied from the given lookupData, or { canCreate: false, missingRelations } otherwise.
 */
export function canCreateInline(
	relationEntityType: string,
	lookupData: Record<string, Array<{ id: number; name: string }>>
):
	| { canCreate: true; schema: UploadSchema }
	| { canCreate: false; noSchema: true; missingRelations: string[] }
	| { canCreate: false; noSchema: false; missingRelations: string[] } {
	const schema = getSchemaForRelation(relationEntityType);
	if (!schema) return { canCreate: false, noSchema: true, missingRelations: [] };

	const missingRelations: string[] = [];
	for (const field of schema.fields) {
		if (field.required && field.dataType === 'relation' && field.relation) {
			const available = lookupData[field.relation.contextKey];
			if (!available || available.length === 0) {
				missingRelations.push(field.label);
			}
		}
	}

	if (missingRelations.length > 0) return { canCreate: false, noSchema: false, missingRelations };
	return { canCreate: true, schema };
}

/** List all available entity types for upload */
export function getAvailableEntityTypes(): Array<{ key: string; label: string }> {
	return Object.entries(uploadSchemaRegistry).map(([key, schema]) => ({
		key,
		label: schema.label,
	}));
}

// Re-export individual schemas
export { v2ActivitySchema } from './v2Activity';
export { v2CategorySchema } from './v2Category';
export { v2ComponentSchema } from './v2Component';
export { v2ConditionSchema } from './v2Condition';
export { v2ContractSchema } from './v2Contract';
export { v2ExpenseTypeSchema } from './v2ExpenseType';
export { v2SpendPlanSchema } from './v2SpendPlan';
export { v2VoucherSchema } from './v2Voucher';
export { v2WorkPlanSchema } from './v2WorkPlan';
export { v1EntitySchema } from './v1Entity';
export { v1SupplierSchema } from './v1Supplier';
export { v1BeneficiarySchema } from './v1Beneficiary';
